import { ColumnProps } from "antd/lib/table";
import { ReactNode, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import ReportContext from "../../../context/report/context";
import { IReport } from "../../../models/report/response";
import moment from "moment";
import Tag from "../../general/antd/tag";
import { getReportType, getReportTypeColor } from "../../../models/report/enum";
import Image from "../../general/antd/image";
import srcImage from "../../../assets/images/exel.png";
import { Button, Col, Modal, Popover, Row, Space, message } from "antd";
import { CheckCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import styles from "./styles.module.scss";
import EndPoints from "../../../services/end-points";
import AppContext from "../../../context/app/context";

interface IProps {}

const FILTER_TRANSLATION_KEY_ALIASES: Record<string, string> = {
  includePhysicalCount: "daily_count_include",
  rScores: "r_score",
  fScores: "f_score",
  mScores: "m_score",
  storeIds: "stores",
  storeFilterMode: "store_filter_mode",
  categoryIds: "categories",
  daypartKeys: "shopping_dayparts",
  deliveryTypes: "delivery_type",
  couponCode: "code",
  discountCodeId: "discount_code",
  areaIds: "areas",
  clientCreatedStartDate: "client_created_start_date",
  clientCreatedEndDate: "client_created_end_date",
  orderStartDate: "order_start_date",
  orderEndDate: "order_end_date",
  coverageAreaIds: "coverage_areas",
  coverageAreaSource: "coverage_area_source",
  includeOutsideCoverageAreas: "include_outside_coverage_areas",
  discountAreaIds: "discount_areas",
  discountAreaSource: "discount_area_source",
  includeOutsideDiscountAreas: "include_outside_discount_areas",
  any: "area_source_any",
  primaryAddress: "area_source_primary_address",
  lastPaidOrder: "area_source_last_paid_order",
  mostFrequentPaidOrder: "area_source_most_frequent_paid_order",
  store_id: "store",
  receiptStatus: "order_payment_receipt_status",
  date: "item_movement_date",
  trademark_id: "trademark",
  product_status: "status",
  store_quantity_status: "store_status",
  driverId: "delivery_worker",
  remaining_days: "remaining_days_until_expiry",
  batchStockFilterMode: "batch_stock_filter_heading",
  last_order_store: "rfm_last_order_store",
  dominant_store_by_orders: "rfm_dominant_store_by_orders",
  dominant_store_by_value: "rfm_dominant_store_by_value",
  express: "express_delivery",
  normal: "normal_delivery",
  select: "coupon_select_from_list",
  manual: "coupon_enter_manually",
  expiration: "batch_stock_filter_expiration",
  remaining: "batch_stock_filter_remaining",
};

const ReportList: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);

  const { list, actions, loading, query } = useContext(ReportContext);
  const [downloadingReportId, setDownloadingReportId] = useState<number | null>(
    null,
  );
  const [confirmingReportId, setConfirmingReportId] = useState<number | null>(
    null,
  );
  const [retryingReportId, setRetryingReportId] = useState<number | null>(null);

  const formatDate = (value?: string | null) => {
    return value ? moment(value).format("YYYY-MM-DD") : "--";
  };

  const normalizeUrl = (value?: string | null) =>
    value?.replace("http://", "https://") ?? null;

  const getTranslationCandidates = (value: string) => {
    const normalizedValue = value
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim();
    const words = normalizedValue.split(/\s+/).filter(Boolean);
    const normalizedWords = words.map(
      (word) => word.charAt(0).toLowerCase() + word.slice(1),
    );
    const camelCaseValue = words
      .map((word, index) => {
        const lowerWord = word.charAt(0).toLowerCase() + word.slice(1);

        if (index === 0) return lowerWord;

        return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
      })
      .join("");
    const snakeCaseValue = normalizedWords.join("_");
    const initialCandidates = [
      value,
      value.trim(),
      normalizedValue,
      camelCaseValue,
      snakeCaseValue,
    ];
    const aliasCandidates = initialCandidates
      .map((candidate) => FILTER_TRANSLATION_KEY_ALIASES[candidate])
      .filter(Boolean);

    return Array.from(
      new Set([...initialCandidates, ...aliasCandidates]),
    ).filter(Boolean);
  };

  const translateFilterText = (value: string) => {
    for (const candidate of getTranslationCandidates(value)) {
      const translated = t(candidate);

      if (translated !== candidate) return translated;
    }

    return value;
  };

  const getReadableFilterLabel = (key: string) => {
    const translated = translateFilterText(key);

    if (translated !== key) return translated;

    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim();
  };

  const formatFilterValue = (value: any) => {
    if (Array.isArray(value)) return value.length ? value.join(", ") : "--";
    if (value === null || value === undefined || value === "") return "--";
    if (typeof value === "boolean") return t(value ? "yes" : "no");
    if (typeof value === "object") return JSON.stringify(value);
    if (typeof value === "string") return translateFilterText(value);

    return String(value);
  };

  const renderFilterValue = (key: string, value: any): ReactNode => {
    if (value === null || value === undefined || value === "") return "--";
    if (key === "receiptStatus") {
      return t(`order_payment_receipt_${value}`);
    }

    if (Array.isArray(value)) {
      if (!value.length) return "--";

      return (
        <div className={styles.filterPills}>
          {value.map((item, index) => (
            <span className={styles.filterPill} key={`${key}-${index}`}>
              {formatFilterValue(item)}
            </span>
          ))}
        </div>
      );
    }

    if (typeof value === "object") {
      const nestedEntries = Object.entries(value);
      const enabledEntries =
        key === "columns"
          ? nestedEntries.filter(([, nestedValue]) => Boolean(nestedValue))
          : nestedEntries;

      if (!enabledEntries.length) return "--";

      const isBooleanMap = enabledEntries.every(
        ([, nestedValue]) => typeof nestedValue === "boolean",
      );

      if (isBooleanMap) {
        return (
          <div className={styles.filterPills}>
            {enabledEntries.map(([nestedKey]) => (
              <span className={styles.filterPill} key={nestedKey}>
                {getReadableFilterLabel(nestedKey)}
              </span>
            ))}
          </div>
        );
      }

      return (
        <div className={styles.nestedFilterList}>
          {enabledEntries.map(([nestedKey, nestedValue]) => (
            <div className={styles.nestedFilterRow} key={nestedKey}>
              <span className={styles.nestedFilterLabel}>
                {getReadableFilterLabel(nestedKey)}
              </span>
              <span className={styles.nestedFilterValue}>
                {formatFilterValue(nestedValue)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if (key.toLowerCase().includes("date")) return formatDate(String(value));

    return formatFilterValue(value);
  };

  const getStatusLabel = (status?: string | null, hasLegacyFile?: boolean) => {
    if (!status && hasLegacyFile) return "report_status_available";

    switch (status) {
      case "pending":
        return "report_status_pending";
      case "processing":
        return "report_status_processing";
      case "available":
        return "report_status_available";
      case "confirmed":
        return "report_status_confirmed";
      case "failed":
        return "report_status_failed";
      case "expired":
        return "report_status_expired";
      case "cancelled":
        return "report_status_cancelled";
      default:
        return "report_status_pending";
    }
  };

  const getStatusColor = (status?: string | null, hasLegacyFile?: boolean) => {
    if (!status && hasLegacyFile) return "green";

    switch (status) {
      case "available":
        return "green";
      case "confirmed":
        return "blue";
      case "failed":
        return "red";
      case "expired":
      case "cancelled":
        return "default";
      case "processing":
        return "blue";
      case "pending":
      default:
        return "gold";
    }
  };

  const getFilenameFromContentDisposition = (contentDisposition?: string) => {
    if (!contentDisposition) return undefined;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return filenameMatch?.[1];
  };

  const getReportFilename = (record: IReport, contentDisposition?: string) => {
    const headerFilename =
      getFilenameFromContentDisposition(contentDisposition)?.trim();
    if (headerFilename) return headerFilename.replace(/[\\/:*?"<>|]/g, "-");

    const extension = String(
      record?.report?.extension ?? record?.extension ?? "xlsx",
    )
      .replace(/^\./, "")
      .trim();
    const reportName = t(getReportType(record.type))
      .replace(/[\\/:*?"<>|]/g, "-")
      .trim();

    return `${reportName || "report"}-${record.id}.${extension || "xlsx"}`;
  };

  const saveBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReport = async (record: IReport, downloadUrl: string) => {
    setDownloadingReportId(record.id);

    try {
      const response = await EndPoints.report.downloadReport(downloadUrl);
      const contentDisposition = response.headers?.["content-disposition"];
      const filename = getReportFilename(record, contentDisposition);
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data]);

      saveBlob(blob, filename);
      await actions.getData();
    } catch {
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleConfirmExternalBilling = (record: IReport) => {
    if (record.canConfirmBilling !== true) return;

    Modal.confirm({
      rootClassName:
        direction === "rtl" ? "external-billing-rtl-confirm" : undefined,
      title: t("external_billing_confirm_title"),
      content: t("external_billing_confirm_message"),
      okText: t("external_billing_confirm_action"),
      cancelText: t("cancel"),
      okButtonProps: { danger: true },
      async onOk() {
        setConfirmingReportId(record.id);
        try {
          await EndPoints.report.confirmExternalBillingReport(record.id);
          message.success(t("external_billing_confirmed_successfully"));
          await actions.getData();
        } finally {
          setConfirmingReportId(null);
        }
      },
    });
  };

  const handleRetryExternalBilling = async (record: IReport) => {
    if (record.canRetry !== true) return;
    setRetryingReportId(record.id);

    try {
      await EndPoints.report.retryExternalBillingReport(record.id);
      message.success(t("external_billing_retry_started"));
      await actions.getData();
    } finally {
      setRetryingReportId(null);
    }
  };

  const columns: ColumnProps<IReport>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      width: 100,
      align: "center",
    },
    {
      title: t("type"),
      dataIndex: "type",
      key: "type",
      align: "center",
      width: 100,
      render: (type) => {
        return (
          <Tag
            title={t(getReportType(type))}
            color={getReportTypeColor(type)}
          />
        );
      },
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 120,
      render: (status, record) => {
        const hasLegacyFile = Boolean(record?.report?.url ?? record?.url);

        return (
          <Tag
            title={getStatusLabel(status, hasLegacyFile)}
            color={getStatusColor(status, hasLegacyFile)}
          />
        );
      },
    },
    {
      title: t("filters"),
      dataIndex: "filters",
      key: "filters",
      align: "center",
      width: 120,
      render: (filters) => {
        const filterEntries = Object.entries(filters ?? {});

        if (!filterEntries.length) return <>--</>;

        return (
          <Popover
            trigger="click"
            placement="bottom"
            overlayClassName={styles.filtersPopover}
            content={
              <div className={styles.filtersPopoverContent}>
                {filterEntries.map(([key, value]) => (
                  <div className={styles.filterDetailRow} key={key}>
                    <div className={styles.filterDetailLabel}>
                      {getReadableFilterLabel(key)}
                    </div>
                    <div className={styles.filterDetailValue}>
                      {renderFilterValue(key, value)}
                    </div>
                  </div>
                ))}
              </div>
            }
          >
            <Button size="small">{t("view")}</Button>
          </Popover>
        );
      },
    },
    {
      title: t("start_date"),
      dataIndex: "startDate",
      key: "startDate",
      align: "center",
      width: 100,
      render: (startDate) => {
        return <>{formatDate(startDate)}</>;
      },
    },
    {
      title: t("end_date"),
      dataIndex: "endDate",
      key: "endDate",
      align: "center",
      width: 100,
      render: (endDate) => {
        return <>{formatDate(endDate)}</>;
      },
    },
    {
      title: t("report"),
      dataIndex: "report",
      key: "report",
      align: "center",
      width: 100,
      render: (report, record) => {
        const legacyFileUrl = normalizeUrl(report?.url ?? record?.url ?? null);
        const downloadUrl = normalizeUrl(record?.downloadUrl);
        const isLegacyDownload = !record?.status && Boolean(legacyFileUrl);
        const isAvailableDownload =
          ["available", "confirmed"].includes(record?.status ?? "") &&
          record?.canDownload === true &&
          Boolean(downloadUrl);
        const fileUrl = isAvailableDownload
          ? downloadUrl
          : isLegacyDownload
            ? legacyFileUrl
            : null;

        if (record?.status === "failed") {
          return (
            <Popover
              content={
                <div>{record?.failureReason ?? t("report_failed_message")}</div>
              }
            >
              <span>
                <Tag title="report_status_failed" color="red" />
              </span>
            </Popover>
          );
        }

        if (record?.status === "expired") {
          return <Tag title="report_status_expired" color="default" />;
        }

        if (record?.status === "cancelled") {
          return <Tag title="report_status_cancelled" color="default" />;
        }

        if (!fileUrl) {
          if (
            record?.status &&
            !["pending", "processing"].includes(record.status)
          ) {
            return (
              <Tag title="report_download_not_available" color="default" />
            );
          }

          return <Tag title="report_is_being_prepared" color="gold" />;
        }

        return (
          <Popover content={<div>{t("click_here_to_download_file")}</div>}>
            {isAvailableDownload ? (
              <Button
                type="text"
                className={styles.downloadButton}
                loading={downloadingReportId === record.id}
                onClick={() => handleDownloadReport(record, downloadUrl)}
              >
                <Row justify="center">
                  <Col span={5}>
                    <div className={styles.fileCardContainer}>
                      <Image src={srcImage} className={styles?.imgFile} />
                    </div>
                  </Col>
                </Row>
              </Button>
            ) : (
              <a href={fileUrl} download style={{ width: "50px" }}>
                <Row justify="center">
                  <Col span={5}>
                    <div className={styles.fileCardContainer}>
                      <Image src={srcImage} className={styles?.imgFile} />
                    </div>
                  </Col>
                </Row>
              </a>
            )}
          </Popover>
        );
      },
    },
    {
      title: t("actions"),
      key: "externalBillingActions",
      align: "center",
      width: 190,
      render: (_, record) => {
        if (record.type !== "externalBilling") return <>--</>;

        return (
          <Space size={4} wrap>
            {record.canConfirmBilling === true ? (
              <Button
                danger
                size="small"
                icon={<CheckCircleOutlined />}
                loading={confirmingReportId === record.id}
                disabled={
                  retryingReportId === record.id ||
                  downloadingReportId === record.id
                }
                onClick={() => handleConfirmExternalBilling(record)}
              >
                {t("external_billing_confirm_action")}
              </Button>
            ) : null}
            {record.canRetry === true ? (
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={retryingReportId === record.id}
                disabled={confirmingReportId === record.id}
                onClick={() => handleRetryExternalBilling(record)}
              >
                {t("external_billing_retry")}
              </Button>
            ) : null}
            {record.canConfirmBilling !== true && record.canRetry !== true
              ? "--"
              : null}
          </Space>
        );
      },
    },
  ];

  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters,
          );
        }}
      />
    </>
  );
};

export default ReportList;
